import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "~/components/logo";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import authClient from "~/lib/auth/auth-client";
import { $checkPasswordResetEligibility } from "~/lib/auth/functions";

export const Route = createFileRoute("/(auth-pages)/forgot-password")({
  component: ForgotPasswordForm,
});

function ForgotPasswordForm() {
  const navigate = useNavigate();

  const { mutate: forgotPasswordMutate, isPending } = useMutation({
    mutationFn: async (data: { email: string }) => {
      const eligibility = await $checkPasswordResetEligibility({
        data: { email: data.email },
      });

      if (!eligibility.eligible && eligibility.reason === "social") {
        const providerNames = eligibility.providers
          ?.map((p) => (p === "github" ? "GitHub" : p === "google" ? "Google" : p))
          .join(" or ");
        throw new Error(
          `This account was created with ${providerNames}. Please login with that provider instead.`,
        );
      }

      await authClient.requestPasswordReset({
        email: data.email,
        redirectTo: `${window.location.origin}/reset-password`,
      });
    },
    onSuccess: () => {
      toast.success("If an account exists, you will receive a password reset email.");
      navigate({ to: "/login" });
    },
    onError: (error) => {
      toast.error(error.message || "An error occurred.");
    },
  });

  const handleSubmit = (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isPending) return;

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;

    if (!email) return;

    forgotPasswordMutate({ email });
  };

  return (
    <div className="flex flex-col gap-6">
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col items-center gap-4 py-4">
            <a href="/" className="flex flex-col items-center gap-2 font-medium">
              <Logo size="lg" showText={false} />
              <span className="sr-only">quickprompt.dev</span>
            </a>
            <h1 className="text-2xl font-bold tracking-tight">Forgot your password?</h1>
            <p className="text-muted-foreground text-center text-sm">
              Enter your email and we&apos;ll send you a reset link.
            </p>
          </div>
          <div className="flex flex-col gap-5">
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="hello@quickprompt.dev"
                readOnly={isPending}
                required
              />
            </div>
            <Button type="submit" className="mt-2 w-full" size="lg" disabled={isPending}>
              {isPending && <LoaderCircle className="animate-spin" />}
              {isPending ? "Sending..." : "Send reset link"}
            </Button>
          </div>
        </div>
      </form>

      <div className="text-center text-sm">
        Remember your password?{" "}
        <Link to="/login" className="underline underline-offset-4">
          Login
        </Link>
      </div>
    </div>
  );
}
